import { MENU_SCAN_MAX_FILES } from '@pulsify/shared/dto'
import { Camera, FileText, ImageOff, Plus, Upload, X } from 'lucide-react'
import { type ChangeEvent, type DragEvent, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'
import {
  fileKey,
  isPdfFile,
  MENU_FILE_ACCEPT,
  type PickRejection,
  pickMenuFiles,
} from '../lib/pick-files'

const REJECTION_MESSAGES: Record<PickRejection, (name: string) => string> = {
  unsupported: (name) => m.menuFileUnsupported({ name }),
  too_many: () => m.menuFileTooMany({ count: MENU_SCAN_MAX_FILES }),
  second_pdf: () => m.menuFileSecondPdf(),
  too_large: (name) => m.menuFilePdfTooLarge({ name }),
}

function useObjectUrl(file: File | null): string | null {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!file) return
    const next = URL.createObjectURL(file)
    setUrl(next)
    return () => URL.revokeObjectURL(next)
  }, [file])
  return url
}

export function FileThumb({ file, onRemove }: { file: File; onRemove?: () => void }) {
  const pdf = isPdfFile(file)
  const url = useObjectUrl(pdf ? null : file)
  const [broken, setBroken] = useState(false)

  return (
    <div className="relative aspect-[4/5] overflow-hidden rounded-lg border bg-muted">
      {pdf || broken || !url ? (
        <div className="flex size-full flex-col items-center justify-center gap-2 px-2 text-center text-muted-foreground">
          {pdf ? (
            <FileText className="size-7" aria-hidden />
          ) : (
            <ImageOff className="size-7" aria-hidden />
          )}
          <span className="line-clamp-2 break-all text-[11px]">{file.name}</span>
        </div>
      ) : (
        <img
          src={url}
          alt={file.name}
          onError={() => setBroken(true)}
          className="size-full object-cover"
        />
      )}
      {onRemove && (
        <button
          type="button"
          aria-label={m.menuEditorRemoveItem({ name: file.name })}
          onClick={onRemove}
          className="absolute top-1.5 right-1.5 flex size-6 items-center justify-center rounded-full bg-card/90 text-muted-foreground shadow-sm transition-colors hover:text-destructive"
        >
          <X className="size-3.5" aria-hidden />
        </button>
      )}
    </div>
  )
}

interface MenuFilePickerProps {
  files: File[]
  onChange: (files: File[]) => void
}

export function MenuFilePicker({ files, onChange }: MenuFilePickerProps) {
  const pickerRef = useRef<HTMLInputElement>(null)
  const cameraRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  function add(list: FileList | null) {
    if (!list) return
    const { accepted, rejected } = pickMenuFiles(files, Array.from(list))
    for (const rejection of rejected) {
      toast.error(REJECTION_MESSAGES[rejection.reason](rejection.name))
    }
    if (accepted.length > 0) onChange([...files, ...accepted])
  }

  function picked(event: ChangeEvent<HTMLInputElement>) {
    add(event.target.files)
    event.target.value = ''
  }

  const dropTarget = {
    onDragOver: (event: DragEvent<HTMLElement>) => {
      event.preventDefault()
      setDragging(true)
    },
    onDragLeave: (event: DragEvent<HTMLElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false)
    },
    onDrop: (event: DragEvent<HTMLElement>) => {
      event.preventDefault()
      setDragging(false)
      add(event.dataTransfer.files)
    },
  }

  return (
    <div className="flex flex-col gap-3">
      <input
        ref={pickerRef}
        type="file"
        accept={MENU_FILE_ACCEPT}
        multiple
        className="hidden"
        onChange={picked}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={picked}
      />
      {files.length === 0 ? (
        <button
          type="button"
          onClick={() => pickerRef.current?.click()}
          {...dropTarget}
          className={cn(
            'flex w-full flex-col items-center gap-3 rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors duration-200 ease-spring',
            'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand/40',
            dragging
              ? 'border-brand bg-brand-subtle/50'
              : 'border-border hover:border-brand/50 hover:bg-brand-subtle/20',
          )}
        >
          <span className="flex size-12 items-center justify-center rounded-full bg-brand-subtle text-brand-subtle-foreground">
            <Upload className="size-5" aria-hidden />
          </span>
          <span className="font-medium text-foreground">
            {dragging ? m.menuDropActive() : m.menuDropTitle()}
          </span>
          <span className="max-w-sm text-muted-foreground text-sm">
            {m.menuDropHint({ count: MENU_SCAN_MAX_FILES })}
          </span>
        </button>
      ) : (
        <ul
          {...dropTarget}
          className={cn(
            'grid grid-cols-3 gap-2 rounded-xl border-2 border-dashed p-3 transition-colors duration-200 ease-spring sm:grid-cols-4 lg:grid-cols-6',
            dragging ? 'border-brand bg-brand-subtle/50' : 'border-border',
          )}
        >
          {files.map((file) => (
            <li key={fileKey(file)}>
              <FileThumb
                file={file}
                onRemove={() => onChange(files.filter((entry) => entry !== file))}
              />
            </li>
          ))}
          {files.length < MENU_SCAN_MAX_FILES && (
            <li>
              <button
                type="button"
                onClick={() => pickerRef.current?.click()}
                className="flex aspect-[4/5] w-full flex-col items-center justify-center gap-1 rounded-lg border border-dashed text-muted-foreground transition-colors hover:border-brand/50 hover:text-foreground"
              >
                <Plus className="size-5" aria-hidden />
                <span className="text-xs">{m.menuFileAdd()}</span>
              </button>
            </li>
          )}
        </ul>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={files.length >= MENU_SCAN_MAX_FILES}
        onClick={() => cameraRef.current?.click()}
        className="hidden w-fit pointer-coarse:inline-flex"
      >
        <Camera />
        {m.menuTakePhoto()}
      </Button>
    </div>
  )
}
